/**
 * File for the `Customer assistance tickets` widget form.
 */

import { Col, DatePicker, Form, Row, Select, Divider, Input, InputNumber } from 'antd';
import { filter, get, isUndefined, map, toLower, forEach, capitalize, isEmpty } from 'lodash';
import moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DynamicObject } from '../../utils/commonInterfaces';
import OrganisationWidgetCommonFilters from './organisation/OrganisationWidgetCommonFilters';
import { getCustomerUILabel } from '../../store/customers/sagas';
import { CUSTOM_FIELD_TYPES } from '../../config/tableAndPageConstants';
import { customFieldIndicator, ticketFieldIndicator } from '../common/FilterBar';
import { getTicketsWidgetFilters } from '../../constants/customerAssistanceSortAndFilters';
import InputAutoCompleteWithButtonDashboard from '../common/InputAutoCompleteWithButtonDashboard';
import { dateSelectOptions } from '../../constants/invoicesSortAndFilters';
import { dateFormatDDMMYYYYSlash, dateFormatYYYYMMDDDash } from '../../constants/dateFormats';
import { populatePopoverContainer, replaceInstancesOfCustomerString } from '../../utils/commonFunctions';
import SelectReadonly from '../FormComponents/SelectReadonly';
import { getTicketOptionsRequestAction } from '../../store/notifications/actions';
import { ApplicationState } from '../../store';

const { Item: FormItem } = Form;
const { Option } = Select;

interface IProps {
    widgetDetails: DynamicObject;
    getFieldDecorator: any;
    drawerRef: any;
    formDisabled?: boolean;
    customFieldsFilterList: DynamicObject[];
    isOrgView?: boolean;
    setFieldsValue: any;
    getFieldValue: any;
}

const OpenSupportTicketsWidgetFields: React.FC<IProps> = ({
    widgetDetails,
    getFieldDecorator,
    formDisabled,
    customFieldsFilterList,
    drawerRef,
    isOrgView,
    getFieldValue,
    setFieldsValue,
}) => {
    const dispatch = useDispatch();
    const customerLabel = useSelector(getCustomerUILabel);

    const [searchFilters, setSearchFilters] = useState<DynamicObject>({});
    
    /**
    * Function for getting the company Id of the currently selected user company.
    */
    const companyId = useSelector((state: ApplicationState) =>
        get(state.companies.selectedUserCompany, 'Company.CompanyId')
    );

    const organisationCompanies = useSelector(
        (app: ApplicationState) => app.organisations.companies.data
    );

    const [ticketReasons, setTicketReasons] = useState(
        [
            {
                label: '',
                value: ''
            }
        ]
    );

    /**
    * Function called to get the ticket reasons needed for the dropdown inside the drawer.
    */
    const updateTicketReasons = (ticketReasonsObject: any[]) => {
        setTicketReasons(
            ticketReasonsObject
        );
    };

    useEffect(() => {
        (async () => {
            await getTicketReasons();
        })();
    }, [companyId, isOrgView]);

    /**
     * Function for setting the `ticket reasons` values and display value
     */
    const getTicketReasons = async () => {
        let allReasons: any[] = [];
        if (isOrgView) {
            for (const company of organisationCompanies) {
                const reasons = await fetchTicketReasons(company.CompanyId);
                reasons.forEach((item: any) => {
                    const exists = allReasons.some((reasonItem) => reasonItem.value === item.value); // get all unique ticket reasons from all company
                    if (!exists) {
                        allReasons.push(item);
                    }
                })
            }
        }
        else {
            const reasons = await fetchTicketReasons(companyId);
            allReasons.push(...reasons);
        }
        updateTicketReasons(allReasons);
    };

    const fetchTicketReasons = async (companyId: any) => {
        try {
            const res: any = await new Promise((resolve, reject) => {
                dispatch(getTicketOptionsRequestAction(companyId, resolve));
            });

            const filteredRes = res.filter((r: DynamicObject) => !r.Hidden);

            const reasons = filteredRes.map((ticketReason: any) => ({
                label: ticketReason.Reason,
                value: ticketReason.Reason,
            }));

            return reasons;
        } catch (error) {
            console.error('Error fetching ticket options:', error);
            return [];
        }
    };

    /**
   * Function that updates the search input filters.
   * @param filterName - name of filter item
   * @param value - string value entered
   */
    const changeSearchFilter = (filterName: string, value: any) => {
        updateSearchFiltersObject({
            [filterName]: value,
        });
    };

    /**
     * Common function for updating the serachFilters object from state.
     * @param searchFiltersObject
     */
    const updateSearchFiltersObject = (searchFiltersObject: DynamicObject) => {
        setSearchFilters({
            ...searchFilters,
            ...searchFiltersObject,
        });
    };

    /**
    * Function that will be triggered when selecting a date range.
    * @param rangeType  - From and To - based on UI mockup
    * @param filterName - name of filter
    */
    const selectDateRange = (rangeType: 'From' | 'To', filterName: string) => {
        return (dateValue: any) => {
            const searchFilterParent = get(searchFilters, filterName);
            const newSearchFilterParent = {
                ...searchFilterParent,
                [rangeType]: dateValue,
            };
            updateSearchFiltersObject({
                [filterName]: newSearchFilterParent,
            });
        };
    };

    /**
     * Function responsible for setting the days range filter values.
     * @param rangeType - Last, Next
     * @param filterName - name of filter
     * @param daysType  - From, To, or not defined
     */
    const selectDaysRange = (
        rangeType: 'Last' | 'Next',
        filterName: string,
        daysType: 'From' | 'To' | undefined = undefined
    ) => {
        return (daysValue: number | undefined) => {
            if (daysType) {
                const searchFilterParent = get(searchFilters, filterName);
                if (get(searchFilterParent, daysType)) {
                    if (rangeType === 'Last') {
                        delete searchFilterParent[daysType].Next;
                    } else {
                        delete searchFilterParent[daysType].Last;
                    }
                }
                const newSearchFilterParent = {
                    ...searchFilterParent,
                    [daysType]: {
                        [rangeType]: daysValue,
                    },
                };

                updateSearchFiltersObject({
                    [filterName]: newSearchFilterParent,
                });
            } else {
                const searchFilterParent = get(searchFilters, filterName);
                if (rangeType === 'Last') {
                    delete searchFilterParent.Next;
                } else {
                    delete searchFilterParent.Last;
                }
                const newSearchFilterParent = {
                    ...searchFilterParent,
                    [rangeType]: daysValue,
                };

                updateSearchFiltersObject({
                    [filterName]: newSearchFilterParent,
                });
            }
        };
    };

    /**
      * Function for handling the select onChange event.
      * @param selectValue - selected value on dropdown
      * @param filter - name of the filter
      * @param fieldKey - key for the component
      * @param stateName - state name of the filter
      */
    const handleSelectOnChange = (
        selectValue: string,
        filter: any,
        fieldKey: string,
        stateName: string
    ) => {
        const newFilterValue: any = {
            value: selectValue,
        };
        if (
            selectValue ===
            dateSelectOptions.CUSTOM_DAYS_RANGE
        ) {
            const customType =
                filter.filterOptionsCustomTypes[selectValue];

            if (customType === 'all-days-range-from-to') {
                if (get(widgetDetails, `${fieldKey}${"--RangeTypeFrom"}`) === "Last") {
                    newFilterValue.From = {
                        Last: get(widgetDetails, `${fieldKey}${"--Last--From"}`)
                    }
                }
                else {
                    newFilterValue.From = {
                        Next: get(widgetDetails, `${fieldKey}${"--Next--From"}`)
                    }
                }

                if (get(widgetDetails, `${fieldKey}${"--RangeTypeTo"}`) === "Last") {
                    newFilterValue.To = {
                        Last: get(widgetDetails, `${fieldKey}${"--Last--To"}`)
                    }
                }
                else {
                    newFilterValue.To = {
                        Next: get(widgetDetails, `${fieldKey}${"--Next--To"}`)
                    }
                }
            }
            else if (customType === 'all-days-range-from-to-last') {
                newFilterValue.From = {
                    Last: get(widgetDetails, `${fieldKey}${"--Last--From"}`)
                };
                newFilterValue.To = {
                    Last: get(widgetDetails, `${fieldKey}${"--Last--To"}`)
                };
            }
            else if (customType === 'last-days-range') {
                newFilterValue.Last = get(widgetDetails, `${fieldKey}${"--Last"}`);
            }
            else if (customType === 'all-days-range') {
                if (get(widgetDetails, `${fieldKey}${"--RangeType"}`) === "Last") {
                    newFilterValue.Last = get(widgetDetails, `${fieldKey}${"--Last"}`);
                }
                else {
                    newFilterValue.Next = get(widgetDetails, `${fieldKey}${"--Next"}`);
                }
            }
            else {
                newFilterValue.Last = 1;
            }
        }

        changeSearchFilter(
            stateName,
            newFilterValue
        );
    }

    /**
    * Function to populate the select/dropdown filter.
    * @param customType - nype of filter, date-range or etc
    * @param filterName - name of filter
    * @param filterFieldKey - key for the component
    * @param filterMaxDate - optional max date for filter, can be a moment object or string
    */
    const populateSelectCustomType = (
        customType: string,
        filterName: string,
        filterFieldKey: string,
        filterMaxDate?: any
    ) => {
        if (customType === 'date-range') {
            const fromDateFieldKey = `${filterFieldKey}${"--From"}`
            const toDateFieldKey = `${filterFieldKey}${"--To"}`
            let disabledDate;
            if (filterMaxDate) {
                disabledDate = (dateValue: any) => {
                    return moment(
                        moment(dateValue).format(dateFormatYYYYMMDDDash)
                    ).isAfter(
                        moment(
                            moment(filterMaxDate).format(dateFormatYYYYMMDDDash)
                        )
                    );
                };
            }
            return (
                <Col span={12} key={filterFieldKey}>
                    <FormItem label={"From"}>
                        {getFieldDecorator(fromDateFieldKey, {
                            initialValue: !isEmpty(get(widgetDetails, fromDateFieldKey)) ?
                                moment(get(widgetDetails, fromDateFieldKey)) :
                                null,
                        })(
                            <DatePicker
                                className="dashboard-datepicker"
                                format={dateFormatDDMMYYYYSlash}
                                disabledDate={disabledDate}
                                placeholder="Start Date"
                                onChange={selectDateRange('From', filterName)}
                                getCalendarContainer={populatePopoverContainer(
                                    drawerRef
                                )}
                                allowClear={true}
                            />
                        )}
                    </FormItem>
                    <FormItem label={"To"}>
                        {getFieldDecorator(toDateFieldKey, {
                            initialValue: !isEmpty(get(widgetDetails, toDateFieldKey)) ?
                            moment(get(widgetDetails, toDateFieldKey)) :
                            null,
                        })(
                            <DatePicker
                                className="dashboard-datepicker"
                                format={dateFormatDDMMYYYYSlash}
                                disabledDate={disabledDate}
                                placeholder="End Date"
                                onChange={selectDateRange('To', filterName)}
                                getCalendarContainer={populatePopoverContainer(
                                    drawerRef
                                )}
                                allowClear={true}
                            />
                        )}
                    </FormItem>
                </Col>
            );
        } else if (customType === 'all-days-range-from-to-last') {
            const daysRangeType = ['Last'];
            const rangeTypeSelectedFrom = 'Last';
            const rangeTypeSelectedTo = 'Last';

            const daysValueFrom = get(
                searchFilters,
                `${filterName}.From.${rangeTypeSelectedFrom}`
            );
            const daysValueTo = get(
                searchFilters,
                `${filterName}.To.${rangeTypeSelectedTo}`
            );

            return (
                <Col span={12} key={filterFieldKey}>
                    {map(['From', 'To'], (daysType: 'From' | 'To') => {
                        let typeLabel = 'Selected from';
                        let rangeTypeSelected: 'Last' = rangeTypeSelectedFrom;
                        let daysValue = daysValueFrom;

                        if (daysType === 'To') {
                            typeLabel = 'Go to';
                            rangeTypeSelected = rangeTypeSelectedTo;
                            daysValue = daysValueTo;
                        }

                        const daysTypeFieldKey = `${filterFieldKey}${"--Last--"}${daysType}`

                        return (
                            <div key={daysType}>
                                <span>{typeLabel}</span><br />
                                <div className="dashboard-div">
                                    <FormItem>
                                        <SelectReadonly
                                            readOnly={formDisabled}
                                            onChange={(rangeType: 'Last') => {
                                                selectDaysRange(
                                                    rangeType,
                                                    filterName,
                                                    daysType
                                                )(1);
                                            }}
                                            value={rangeTypeSelected}
                                            placeholder="Type"
                                            getPopupContainer={populatePopoverContainer(
                                                drawerRef
                                            )}
                                        >
                                            {map(
                                                daysRangeType,
                                                (rangeType: string) => (
                                                    <Option
                                                        key={rangeType}
                                                        value={rangeType}
                                                    >
                                                        {rangeType}
                                                    </Option>
                                                )
                                            )}
                                        </SelectReadonly>
                                    </FormItem>
                                    &nbsp;&nbsp;
                                    <FormItem>
                                        {getFieldDecorator(daysTypeFieldKey, {
                                            initialValue: daysValue
                                        })(
                                            <InputNumber
                                                placeholder="Count"
                                                onChange={selectDaysRange(
                                                    rangeTypeSelected,
                                                    filterName,
                                                    daysType
                                                )}
                                                min={1}
                                            />
                                        )}
                                    </FormItem>
                                    &nbsp;&nbsp;
                                    <span>
                                        day
                                        {daysValue > 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                    }
                </Col >
            );
        }
    };

    /**
    * Function for populating the ticket type options.
    */
    const populateTicketTypesSelectOptions = () =>
        map(ticketReasons, ({ label, value }: any) => {
            const labelUsed = replaceInstancesOfCustomerString(
                label,
                customerLabel,
                isOrgView
            );
            return (
                <Option key={value} value={value.toString()}>
                    {labelUsed}
                </Option>
            );
        });

    /**
    * Function for populating the ticket fields filters.
    */
    const populateTicketFieldsFilters = () => {
        return getTicketsWidgetFilters().map(
            (filter: DynamicObject) => {
                const fieldKey = `${ticketFieldIndicator}${filter.filterStateName}`;
                const stateName = filter.filterStateName || filter.filterName;
                if (!filter.filterExclude) {
                    if (filter.filterElement === 'select-checkbox-group') {
                        return (
                            <Col span={12} key={fieldKey}>
                                <FormItem label={filter.filterName}>
                                    {getFieldDecorator(fieldKey, {
                                        initialValue:
                                            get(widgetDetails, fieldKey),
                                    })(
                                        <SelectReadonly
                                            allowClear={true}
                                            readOnly={formDisabled}
                                            placeholder={filter.filterPlaceholder}
                                            getPopupContainer={populatePopoverContainer(
                                                drawerRef
                                            )}
                                            mode="multiple"
                                        >
                                            {populateTicketTypesSelectOptions()}
                                        </SelectReadonly>
                                    )}
                                </FormItem>
                            </Col >
                        );
                    }
                    else if (filter.filterElement === 'select') {
                        const dateTypeFieldKey = `${fieldKey}${"--DateType"}`
                        const optionsObject: any = {};
                        forEach(filter.filterOptions, (option: string) => {
                            if (!optionsObject[option]) {
                                optionsObject[option] = option;
                            }
                        });

                        const selectCustomValueType = !isUndefined(searchFilters[stateName]) ?
                            get(searchFilters[stateName], 'value') :
                            get(widgetDetails, dateTypeFieldKey)

                        const selectCustomType = filter.filterOptionsCustomTypes[selectCustomValueType];

                        // Manually simulate onChange event on initial load
                        if ((selectCustomValueType === dateSelectOptions.CUSTOM_DATE_RANGE ||
                            selectCustomValueType === dateSelectOptions.CUSTOM_DAYS_RANGE) &&
                            isUndefined(searchFilters[stateName])) {
                            handleSelectOnChange(selectCustomValueType, filter, fieldKey, stateName);
                        }
                        return (
                            <Col span={12} key={dateTypeFieldKey}>
                                <FormItem label={filter.filterName}>
                                    {getFieldDecorator(dateTypeFieldKey, {
                                        initialValue: get(
                                            widgetDetails,
                                            dateTypeFieldKey
                                        ),
                                    })(
                                        <SelectReadonly
                                            allowClear={true}
                                            readOnly={formDisabled}
                                            className="date-picker-select"
                                            placeholder={filter.filterPlaceholder}
                                            onChange={(selectValue: any) => {
                                                handleSelectOnChange(selectValue, filter, fieldKey, stateName);
                                            }}
                                            getPopupContainer={populatePopoverContainer(
                                                drawerRef
                                            )}
                                        >
                                            {map(optionsObject, (option: any) => (
                                                <Option key={option} value={option}>
                                                    {!isUndefined(option) ? option : 'Select Date Range'}
                                                </Option>
                                            ))}
                                        </SelectReadonly>
                                    )}
                                </FormItem>
                                {selectCustomType &&
                                    populateSelectCustomType(
                                        selectCustomType,
                                        stateName,
                                        fieldKey,
                                        filter.filterMaxDate
                                    )}
                            </Col >
                        );
                    }
                    else if (filter.filterElement === 'input-autocomplete') {
                        const filterNameUsed =
                            filter.filterStateName === 'Customer' &&
                                !isOrgView
                                ? capitalize(customerLabel)
                                : filter.filterName;
                        const stateName = filter.filterStateName || filter.filterName;
                        return (
                            <Col span={12} key={fieldKey}>
                                <FormItem label={filterNameUsed}>
                                    <InputAutoCompleteWithButtonDashboard
                                        readOnly={formDisabled}
                                        hasNoOkButton={true}
                                        updateField={(value: string) => {
                                            changeSearchFilter(stateName, value);
                                        }}
                                        stateValue={searchFilters[stateName]}
                                        queryName={filter.filterQuery}
                                        queryFilterName={filter.filterNameQuery}
                                        filterField={filter.filterStateName}
                                        sortField={filter.filterSort}
                                        responseName={filter.filterResponse}
                                        labelField={filter.filterLabelField}
                                        placeholder={filter.filterPlaceholder}
                                        filterSubChecking={filter.filterSubChecking}
                                        queryFilterNameSub={filter.filterNameQuerySub}
                                        getFieldDecorator={getFieldDecorator}
                                        widgetDetails={widgetDetails}
                                        filterFieldKey={fieldKey}
                                    />
                                </FormItem>
                            </Col>
                        );
                    }
                    else {
                        const filterNameUsed =
                            filter.filterStateName === 'Customer' &&
                                !isOrgView
                                ? capitalize(customerLabel)
                                : filter.filterName;
                        return (
                            <Col span={12} key={fieldKey}>
                                <FormItem label={filterNameUsed}>
                                    {getFieldDecorator(fieldKey, {
                                        initialValue: get(
                                            widgetDetails,
                                            fieldKey
                                        ),
                                    })(
                                        <Input
                                            placeholder={filterNameUsed}
                                            readOnly={formDisabled}
                                        />
                                    )}
                                </FormItem>
                            </Col>
                        );
                    }
                }
            }
        );
    };

    /**
     * Common function for populating the custom fields filters.
     * @param customFieldType
     */
    const populateCustomFieldsFilters = (customFieldType: string) => {
        return filter(customFieldsFilterList, ['Type', customFieldType]).map(
            ({ Type, FieldName }: DynamicObject) => {
                const customFieldKey = `${customFieldIndicator}${Type}--${FieldName}`;
                return (
                    <Col span={12} key={customFieldKey}>
                        <FormItem label={FieldName}>
                            {getFieldDecorator(customFieldKey, {
                                initialValue: get(
                                    widgetDetails,
                                    customFieldKey
                                ),
                            })(
                                <Input
                                    placeholder={FieldName}
                                    readOnly={formDisabled}
                                />
                            )}
                        </FormItem>
                    </Col>
                );
            }
        );
    };

    return (
        <Row>
            <Col>
                <Row>
                    <Col span={24}>
                        <FormItem label="Filter by ticket reasons">
                            {getFieldDecorator('ticketTypes', {
                                initialValue:
                                    get(widgetDetails, 'ticketTypes')
                            })(
                                <SelectReadonly
                                    readOnly={formDisabled}
                                    placeholder="Select Ticket Reason"
                                    style={{ width: '100%' }}
                                    getPopupContainer={populatePopoverContainer(
                                        drawerRef
                                    )}
                                    mode="multiple"
                                >
                                    {populateTicketTypesSelectOptions()}
                                </SelectReadonly>
                            )}
                        </FormItem>
                    </Col>
                </Row>
                <Divider />
                <Row>
                    <Col>
                        <Row>
                            <Col span={24}>
                                <h3>Filter by ticket fields</h3>
                            </Col>
                        </Row>
                        <Row gutter={10}>
                            {populateTicketFieldsFilters()}
                        </Row>
                    </Col>
                </Row>
                <Divider />
                <Row>
                    <Col>
                        <Row>
                            <Col span={24}>
                                <h3>
                                    Filter by{' '}
                                    {isOrgView
                                        ? 'customer'
                                        : toLower(customerLabel)}{' '}
                                    custom fields
                                </h3>
                            </Col>
                        </Row>
                        <Row gutter={10}>
                            {populateCustomFieldsFilters(
                                CUSTOM_FIELD_TYPES.CUSTOMER
                            )}
                        </Row>
                    </Col>
                </Row>
                {isOrgView && (
                    <OrganisationWidgetCommonFilters
                        widgetDetails={widgetDetails}
                        getFieldDecorator={getFieldDecorator}
                        getFieldValue={getFieldValue}
                        setFieldsValue={setFieldsValue}
                        drawerRef={drawerRef}
                        isRegional
                        noTopDivider
                    />
                )}
            </Col>
        </Row>
    );
};

export default OpenSupportTicketsWidgetFields;
